import AddIcon from "@mui/icons-material/Add";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import MoreTimeIcon from "@mui/icons-material/MoreTime";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { DateCalendar } from "@mui/x-date-pickers";
import DrawerComponentView from "src/app/components/drawer/drawer.view";
import DrawerAwareContainerComponentView from "src/app/components/drawerAwareContainer/drawerAwareContainer.view";
import SnackBarComponentView from "src/app/components/snackbar/snackbar.view";
import { RequestManagementUser } from "src/app/models/screens/requestManagement.model";
import { Task } from "src/app/models/screens/task.model";
import TasksScreenViewModel from "src/app/screens/tasks/tasks.viewmodel";
import styles from "src/app/styles/screens/tasks.style";

const TasksScreenView: React.FC = () => {
  const {
    isSnackbarError,
    snackbarMessage,
    isDisplayingSnackbar,
    setIsDisplayingSnackbar,
    userRole,
    children,
    isLoadingChildren,
    selectedOwnerId,
    onOwnerChanged,
    selectedDate,
    onDateChanged,
    tasks,
    isLoadingTasks,
    minutesToLabel,
    getStatusChipColor,
    canManageTasks,
    isCreateTaskModalOpen,
    openCreateTaskModal,
    closeCreateTaskModal,
    newTaskTitle,
    setNewTaskTitle,
    newTaskDescription,
    setNewTaskDescription,
    newTaskFromHour,
    setNewTaskFromHour,
    newTaskFromMinute,
    setNewTaskFromMinute,
    newTaskToHour,
    setNewTaskToHour,
    newTaskToMinute,
    setNewTaskToMinute,
    isCreatingTask,
    onCreateTaskConfirmed,
    newTaskIsPriority,
    setNewTaskIsPriority,
    tags,
    selectedTagKey,
    toggleTag,
    newTagText,
    setNewTagText,
    newTagColor,
    setNewTagColor,
    onCreateTag,
    normalizeTagKey,
    onMarkTaskStatus,
    onToggleTaskPriority,
    isDeleteModalOpen,
    taskToDelete,
    openDeleteModal,
    closeDeleteModal,
    isDeletingTask,
    onDeleteTaskConfirmed,
    isAssignTaskModalOpen,
    taskToAssign,
    openAssignTaskModal,
    closeAssignTaskModal,
    assignUserSearchText,
    setAssignUserSearchText,
    assignSearchedUsers,
    isSearchingAssignUsers,
    selectedAssignUser,
    setSelectedAssignUser,
    isAssigningTask,
    onAssignTaskConfirmed,
    isMoreTimeModalOpen,
    taskForMoreTime,
    openMoreTimeModal,
    closeMoreTimeModal,
    moreTimeNewDate,
    setMoreTimeNewDate,
    moreTimeNewFromHour,
    setMoreTimeNewFromHour,
    moreTimeNewFromMinute,
    setMoreTimeNewFromMinute,
    moreTimeNewToHour,
    setMoreTimeNewToHour,
    moreTimeNewToMinute,
    setMoreTimeNewToMinute,
    isRequestingMoreTime,
    onMoreTimeConfirmed,
    isCollaboratorModalOpen,
    taskForCollaborator,
    openCollaboratorModal,
    closeCollaboratorModal,
    collaboratorSearchText,
    setCollaboratorSearchText,
    collaboratorSearchedUsers,
    isSearchingCollaborators,
    selectedCollaboratorUser,
    setSelectedCollaboratorUser,
    isAddingCollaborator,
    onAddCollaboratorConfirmed,
    onRemoveCollaborator,
    adminRequestUsers,
    isLoadingAdminUsers,
  } = TasksScreenViewModel();

  const renderTaskStatusActions = (task: Task) => {
    if (!canManageTasks) return null;
    const now = new Date();
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
      2,
      "0",
    )}-${String(now.getDate()).padStart(2, "0")}`;
    const isToday = task.date === today;
    const isPast = task.to <= nowMinutes && isToday;

    if (task.status === "Completed" || task.status === "CompletedOverdue") {
      return (
        <Button
          size="small"
          variant="outlined"
          startIcon={<RadioButtonUncheckedIcon />}
          onClick={() => onMarkTaskStatus(task.id, "Ongoing")}
          disabled={isPast}
        >
          Mark Ongoing
        </Button>
      );
    }

    if (task.status === "Incomplete") {
      return (
        <Button
          size="small"
          variant="contained"
          color="warning"
          startIcon={<CheckCircleOutlineIcon />}
          onClick={() => onMarkTaskStatus(task.id, "CompletedOverdue")}
        >
          Mark Done (Overdue)
        </Button>
      );
    }

    return (
      <Button
        size="small"
        variant="contained"
        color="success"
        startIcon={<CheckCircleOutlineIcon />}
        onClick={() => {
          const status = isPast ? "CompletedOverdue" : "Completed";
          onMarkTaskStatus(task.id, status);
        }}
      >
        Mark Completed
      </Button>
    );
  };

  return (
    <Box sx={styles.parent}>
      <DrawerComponentView selectedId="Tasks" />
      <DrawerAwareContainerComponentView
        drawerWidth={250}
        sx={styles.drawerAwareContainer}
      >
        <Grid container justifyContent="center" spacing={3}>
          <Grid
            size={{ xl: 10, lg: 10, md: 11, sm: 11, xs: 12 }}
            sx={styles.titleGrid}
          >
            <Typography component="span" sx={styles.titleText}>
              Tasks
            </Typography>
          </Grid>

          {userRole === "Parent" && (
            <Grid size={{ xl: 10, lg: 10, md: 11, sm: 11, xs: 12 }}>
              <Grid
                size={{ xl: 4, lg: 5, md: 5, sm: 6, xs: 7 }}
                sx={styles.selectorContainer}
                justifySelf="flex-end"
              >
                <Paper elevation={0} sx={styles.selectorPaper}>
                  <FormControl fullWidth>
                    <InputLabel id="task-owner-select-label">
                      Task Owner
                    </InputLabel>
                    <Select
                      labelId="task-owner-select-label"
                      value={selectedOwnerId}
                      label="Task Owner"
                      onChange={(e: SelectChangeEvent) =>
                        onOwnerChanged(e.target.value)
                      }
                      disabled={isLoadingChildren}
                    >
                      <MenuItem value="you">You</MenuItem>
                      {children.map((child) => (
                        <MenuItem key={child._id} value={child._id}>
                          {child.firstName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Paper>
              </Grid>
            </Grid>
          )}

          {userRole === "Admin" && (
            <Grid size={{ xl: 10, lg: 10, md: 11, sm: 11, xs: 12 }}>
              <Grid
                size={{ xl: 4, lg: 5, md: 5, sm: 6, xs: 7 }}
                sx={styles.selectorContainer}
                justifySelf="flex-end"
              >
                <Paper elevation={0} sx={styles.selectorPaper}>
                  <FormControl fullWidth sx={{ mt: 1 }}>
                    <InputLabel id="admin-task-owner-label">
                      Viewing Tasks Of
                    </InputLabel>
                    <Select
                      labelId="admin-task-owner-label"
                      value={selectedOwnerId}
                      label="Viewing Tasks Of"
                      onChange={(e: SelectChangeEvent) =>
                        onOwnerChanged(e.target.value)
                      }
                      disabled={isLoadingAdminUsers}
                    >
                      <MenuItem value="you">You</MenuItem>
                      {adminRequestUsers.map((user: RequestManagementUser) => (
                        <MenuItem key={user.id} value={user.id}>
                          {`${user.firstName} ${user.lastName}`}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Paper>
              </Grid>
            </Grid>
          )}

          <Grid size={{ xl: 10, lg: 10, md: 11, sm: 11, xs: 12 }}>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6, xl: 4 }}>
                <Paper elevation={0} sx={styles.calendarContainer}>
                  <Typography sx={styles.calendarTitleText}>
                    Select Date
                  </Typography>
                  <DateCalendar value={selectedDate} onChange={onDateChanged} />
                  {canManageTasks && selectedDate && (
                    <Button
                      fullWidth
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={openCreateTaskModal}
                    >
                      Add Task
                    </Button>
                  )}
                </Paper>
              </Grid>

              <Grid size={{ xs: 12, md: 6, xl: 8 }}>
                <Paper elevation={0} sx={styles.selectedTabContent}>
                  <Typography sx={styles.selectedTabText}>
                    {selectedDate
                      ? `Tasks for ${selectedDate.format("MMMM D, YYYY")}`
                      : "Tasks"}
                  </Typography>

                  {!selectedDate ? (
                    <Typography sx={styles.subTitleText}>
                      Select a date to view tasks.
                    </Typography>
                  ) : isLoadingTasks ? (
                    <Box sx={styles.searchingLoader}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : tasks.length === 0 ? (
                    <Typography sx={styles.noTasksText}>
                      No tasks for this date.
                    </Typography>
                  ) : (
                    tasks.map((task: Task) => {
                      const isCompleted =
                        task.status === "Completed" ||
                        task.status === "CompletedOverdue";
                      const isOwner =
                        !task.isAssigned || task.assignedById === undefined;
                      return (
                        <Paper
                          key={task.id}
                          elevation={0}
                          sx={[
                            styles.taskCard,
                            task.status === "Incomplete"
                              ? styles.taskCardOverdue
                              : {},
                            isCompleted ? styles.taskCardCompleted : {},
                            task.isPriority ? styles.taskCardPriority : {},
                          ]}
                        >
                          <Box sx={styles.taskTitleRow}>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                flexWrap: "wrap",
                              }}
                            >
                              {task.isPriority && (
                                <Tooltip title="Priority task">
                                  <PriorityHighIcon
                                    fontSize="small"
                                    color="error"
                                  />
                                </Tooltip>
                              )}
                              <Typography sx={styles.taskTitleText}>
                                {task.title}
                              </Typography>
                            </Box>
                            <Chip
                              size="small"
                              label={task.status}
                              color={getStatusChipColor(task.status)}
                            />
                          </Box>
                          <Box sx={styles.chipRow}>
                            <Chip
                              size="small"
                              label={task.tag}
                              variant="filled"
                              sx={
                                task.tagColor || tags.get(task.tag)
                                  ? {
                                      backgroundColor:
                                        task.tagColor ?? tags.get(task.tag),
                                      color: "#fff",
                                    }
                                  : {}
                              }
                            />
                            {task.isPriority && (
                              <Chip
                                size="small"
                                label="Priority"
                                color="error"
                                variant="outlined"
                              />
                            )}
                          </Box>
                          <Typography sx={styles.taskMetaText}>
                            {`${minutesToLabel(task.from)} – ${minutesToLabel(
                              task.to,
                            )}`}
                          </Typography>
                          <Typography sx={styles.taskDescriptionText}>
                            {task.description}
                          </Typography>
                          {task.isAssigned && task.assignedByFullName && (
                            <Typography sx={[styles.taskMetaText, { mb: 1 }]}>
                              Assigned by: {task.assignedByFullName}
                            </Typography>
                          )}
                          {task.collaborators &&
                            task.collaborators.length > 0 && (
                              <Box sx={{ mb: 1 }}>
                                <Typography
                                  variant="caption"
                                  sx={{ opacity: 0.7 }}
                                >
                                  Collaborators:{" "}
                                  {task.collaborators
                                    .map((c: any) => c.fullName)
                                    .join(", ")}
                                </Typography>
                                {canManageTasks && isOwner && (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexWrap: "wrap",
                                      gap: 0.5,
                                      mt: 0.5,
                                    }}
                                  >
                                    {task.collaborators.map((c: any) => (
                                      <Chip
                                        key={c.id}
                                        size="small"
                                        label={c.fullName}
                                        onDelete={() =>
                                          onRemoveCollaborator(task.id, c.id)
                                        }
                                      />
                                    ))}
                                  </Box>
                                )}
                              </Box>
                            )}

                          {!["Completed", "CompletedOverdue"].includes(
                            task.status,
                          ) && <Divider sx={{ mb: 1.5 }} />}
                          {!["Completed", "CompletedOverdue"].includes(
                            task.status,
                          ) && (
                            <Box sx={styles.taskActionsRow}>
                              {renderTaskStatusActions(task)}
                              {canManageTasks && isOwner && (
                                <Tooltip
                                  title={
                                    task.isPriority
                                      ? "Remove priority"
                                      : "Mark as priority"
                                  }
                                >
                                  <IconButton
                                    size="small"
                                    color={
                                      task.isPriority ? "error" : "default"
                                    }
                                    onClick={() =>
                                      onToggleTaskPriority(task.id)
                                    }
                                  >
                                    <PriorityHighIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              )}

                              {canManageTasks && !task.isAssigned && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<AssignmentIndIcon />}
                                  onClick={() => openAssignTaskModal(task)}
                                >
                                  Assign
                                </Button>
                              )}
                              {canManageTasks &&
                                isOwner &&
                                !task.isAssigned && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<GroupAddIcon />}
                                    onClick={() => openCollaboratorModal(task)}
                                  >
                                    Share
                                  </Button>
                                )}
                              {task.isAssigned &&
                                !task.isDirectlyAssigned &&
                                !task.isPriority &&
                                canManageTasks &&
                                (task.status === "Ongoing" ||
                                  task.status === "Incomplete") && (
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="warning"
                                    startIcon={<MoreTimeIcon />}
                                    onClick={() => openMoreTimeModal(task)}
                                  >
                                    Reschedule Request
                                  </Button>
                                )}

                              {canManageTasks && !task.isAssigned && (
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="error"
                                  startIcon={<DeleteOutlineIcon />}
                                  onClick={() => openDeleteModal(task)}
                                >
                                  Delete
                                </Button>
                              )}
                            </Box>
                          )}
                        </Paper>
                      );
                    })
                  )}
                </Paper>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DrawerAwareContainerComponentView>
      <Dialog
        open={isCreateTaskModalOpen}
        onClose={closeCreateTaskModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Create Task</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Title"
            value={newTaskTitle}
            onChange={(e) => setNewTaskTitle(e.target.value)}
            sx={styles.modalTextField}
          />
          <TextField
            fullWidth
            multiline
            minRows={3}
            label="Description"
            value={newTaskDescription}
            onChange={(e) => setNewTaskDescription(e.target.value)}
            sx={styles.modalTextField}
          />
          <FormControlLabel
            control={
              <Switch
                checked={newTaskIsPriority}
                onChange={(e) => setNewTaskIsPriority(e.target.checked)}
                color="error"
              />
            }
            label="Mark as Priority"
            sx={{ mb: 1 }}
          />
          <Divider sx={{ my: 2 }} />
          <Typography sx={{ mb: 1, fontWeight: 600 }} component="div">
            Tag
          </Typography>
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            sx={{ mb: 2 }}
          >
            {tags.size === 0 ? (
              <Typography variant="body2" sx={{ opacity: 0.7 }}>
                No tags yet. Create one below.
              </Typography>
            ) : (
              Array.from(tags.keys()).map((t: string) => (
                <Chip
                  key={t}
                  label={t}
                  onClick={() => toggleTag(t)}
                  variant={
                    normalizeTagKey(t) === selectedTagKey
                      ? "filled"
                      : "outlined"
                  }
                  sx={{
                    borderColor: tags.get(t),
                    ...(normalizeTagKey(t) === selectedTagKey
                      ? { backgroundColor: tags.get(t), color: "#fff" }
                      : { color: tags.get(t) }),
                  }}
                />
              ))
            )}
          </Stack>
          <Typography sx={{ mb: 1, opacity: 0.8 }} component="div">
            Create new tag
          </Typography>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Tag text"
                value={newTagText}
                onChange={(e) => setNewTagText(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <TextField
                fullWidth
                label="Color"
                type="color"
                value={newTagColor}
                onChange={(e) => setNewTagColor(e.target.value)}
                InputLabelProps={{ shrink: true }}
                sx={styles.colorPicker}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 3 }}>
              <Button
                fullWidth
                variant="outlined"
                disabled={!normalizeTagKey(newTagText)}
                onClick={onCreateTag}
              >
                Add
              </Button>
            </Grid>
          </Grid>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2" sx={{ mb: 1, opacity: 0.7 }}>
            Start Time
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="Hour (0-23)"
                type="number"
                value={newTaskFromHour}
                onChange={(e) => setNewTaskFromHour(e.target.value)}
                inputProps={{ min: 0, max: 23 }}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="Minute (0-59)"
                type="number"
                value={newTaskFromMinute}
                onChange={(e) => setNewTaskFromMinute(e.target.value)}
                inputProps={{ min: 0, max: 59 }}
              />
            </Grid>
          </Grid>
          <Typography variant="body2" sx={{ mb: 1, opacity: 0.7 }}>
            End Time
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="Hour (0-23)"
                type="number"
                value={newTaskToHour}
                onChange={(e) => setNewTaskToHour(e.target.value)}
                inputProps={{ min: 0, max: 23 }}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="Minute (0-59)"
                type="number"
                value={newTaskToMinute}
                onChange={(e) => setNewTaskToMinute(e.target.value)}
                inputProps={{ min: 0, max: 59 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Box sx={styles.modalActionsContainer}>
            <Button
              onClick={closeCreateTaskModal}
              color="inherit"
              variant="outlined"
              sx={styles.modalCancelButton}
            >
              Cancel
            </Button>
            <Button
              onClick={onCreateTaskConfirmed}
              variant="contained"
              loading={isCreatingTask}
            >
              Create
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      <Dialog
        open={isDeleteModalOpen}
        onClose={closeDeleteModal}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Delete Task</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{" "}
            <strong>{taskToDelete?.title}</strong>?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Box sx={styles.modalActionsContainer}>
            <Button
              onClick={closeDeleteModal}
              color="inherit"
              variant="outlined"
              sx={styles.modalCancelButton}
            >
              Cancel
            </Button>
            <Button
              onClick={onDeleteTaskConfirmed}
              variant="contained"
              color="error"
              loading={isDeletingTask}
            >
              Delete
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      <Dialog
        open={isAssignTaskModalOpen}
        onClose={closeAssignTaskModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Assign Task: {taskToAssign?.title}</DialogTitle>
        <DialogContent>
          {taskToAssign?.isPriority && (
            <Typography
              variant="body2"
              color="error"
              sx={{ mb: 1, fontWeight: 600 }}
            >
              This is a priority task. It cannot be assigned to a time that
              conflicts with the recipient's non-free slots.
            </Typography>
          )}
          <Typography sx={{ mb: 1 }}>
            Search for a user to assign this task to.
          </Typography>
          <TextField
            fullWidth
            label="Search users"
            value={assignUserSearchText}
            onChange={(e) => setAssignUserSearchText(e.target.value)}
            sx={styles.modalTextField}
          />
          {isSearchingAssignUsers ? (
            <Box sx={styles.searchingLoader}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {assignSearchedUsers.map((user: RequestManagementUser) => {
                const isSelected = selectedAssignUser?.id === user.id;
                return (
                  <ListItemButton
                    key={user.id}
                    onClick={() => setSelectedAssignUser(user)}
                    sx={[
                      { borderRadius: 2, mb: 0.5 },
                      {
                        backgroundColor: isSelected
                          ? "rgba(25,118,210,0.08)"
                          : "transparent",
                      },
                    ]}
                  >
                    <ListItemText
                      primary={`${user.firstName} ${user.lastName}`}
                    />
                  </ListItemButton>
                );
              })}
              {assignSearchedUsers.length === 0 && (
                <Typography sx={styles.noTasksText}>No users found.</Typography>
              )}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Box sx={styles.modalActionsContainer}>
            <Button
              onClick={closeAssignTaskModal}
              color="inherit"
              variant="outlined"
              sx={styles.modalCancelButton}
            >
              Cancel
            </Button>
            <Button
              onClick={onAssignTaskConfirmed}
              variant="contained"
              disabled={!selectedAssignUser}
              loading={isAssigningTask}
            >
              Assign
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      <Dialog
        open={isMoreTimeModalOpen}
        onClose={closeMoreTimeModal}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Request Reschedule</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>
            Propose a new date and time for{" "}
            <strong>{taskForMoreTime?.title}</strong>.
          </Typography>
          <TextField
            fullWidth
            label="New Date (YYYY-MM-DD)"
            value={moreTimeNewDate}
            onChange={(e) => setMoreTimeNewDate(e.target.value)}
            sx={styles.modalTextField}
            placeholder="e.g. 2025-09-20"
          />
          <Typography variant="body2" sx={{ mb: 1, opacity: 0.7 }}>
            New Start Time
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="Hour (0-23)"
                type="number"
                value={moreTimeNewFromHour}
                onChange={(e) => setMoreTimeNewFromHour(e.target.value)}
                inputProps={{ min: 0, max: 23 }}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="Minute (0-59)"
                type="number"
                value={moreTimeNewFromMinute}
                onChange={(e) => setMoreTimeNewFromMinute(e.target.value)}
                inputProps={{ min: 0, max: 59 }}
              />
            </Grid>
          </Grid>
          <Typography variant="body2" sx={{ mb: 1, opacity: 0.7 }}>
            New End Time
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="Hour (0-23)"
                type="number"
                value={moreTimeNewToHour}
                onChange={(e) => setMoreTimeNewToHour(e.target.value)}
                inputProps={{ min: 0, max: 23 }}
              />
            </Grid>
            <Grid size={{ xs: 6 }}>
              <TextField
                fullWidth
                label="Minute (0-59)"
                type="number"
                value={moreTimeNewToMinute}
                onChange={(e) => setMoreTimeNewToMinute(e.target.value)}
                inputProps={{ min: 0, max: 59 }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Box sx={styles.modalActionsContainer}>
            <Button
              onClick={closeMoreTimeModal}
              color="inherit"
              variant="outlined"
              sx={styles.modalCancelButton}
            >
              Cancel
            </Button>
            <Button
              onClick={onMoreTimeConfirmed}
              variant="contained"
              loading={isRequestingMoreTime}
            >
              Send Request
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      <Dialog
        open={isCollaboratorModalOpen}
        onClose={closeCollaboratorModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Share Task: {taskForCollaborator?.title}</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 1 }}>
            Search for a user to share this task with. Collaborators have the
            same action scope as you (mark complete, etc.).
          </Typography>
          <TextField
            fullWidth
            label="Search users"
            value={collaboratorSearchText}
            onChange={(e) => setCollaboratorSearchText(e.target.value)}
            sx={styles.modalTextField}
          />
          {isSearchingCollaborators ? (
            <Box sx={styles.searchingLoader}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <List sx={{ p: 0 }}>
              {collaboratorSearchedUsers.map((user: RequestManagementUser) => {
                const isSelected = selectedCollaboratorUser?.id === user.id;
                const isAlready = taskForCollaborator?.collaborators.some(
                  (c) => c.id === user.id,
                );
                return (
                  <ListItemButton
                    key={user.id}
                    onClick={() =>
                      !isAlready && setSelectedCollaboratorUser(user)
                    }
                    disabled={isAlready}
                    sx={[
                      { borderRadius: 2, mb: 0.5 },
                      {
                        backgroundColor: isSelected
                          ? "rgba(25,118,210,0.08)"
                          : "transparent",
                      },
                    ]}
                  >
                    <ListItemText
                      primary={`${user.firstName} ${user.lastName}`}
                      secondary={
                        isAlready ? "Already a collaborator" : undefined
                      }
                    />
                  </ListItemButton>
                );
              })}
              {collaboratorSearchedUsers.length === 0 && (
                <Typography sx={styles.noTasksText}>No users found.</Typography>
              )}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Box sx={styles.modalActionsContainer}>
            <Button
              onClick={closeCollaboratorModal}
              color="inherit"
              variant="outlined"
              sx={styles.modalCancelButton}
            >
              Cancel
            </Button>
            <Button
              onClick={onAddCollaboratorConfirmed}
              variant="contained"
              disabled={!selectedCollaboratorUser}
              loading={isAddingCollaborator}
            >
              Share
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <SnackBarComponentView
        isError={isSnackbarError}
        isOpen={isDisplayingSnackbar}
        message={snackbarMessage}
        onClose={() => setIsDisplayingSnackbar(false)}
      />
    </Box>
  );
};

export default TasksScreenView;
